import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import $api from "http";
import { Layout } from "components";
import {
  Search,
  ChevronDown,
  ChevronUp,
  ArrowDownLeft,
  ArrowUpRight,
  FileText,
  Pencil,
  History,
  ClipboardList,
  Clock3,
  Warehouse,
  UserCheck,
  FileCheck2,
  LayoutDashboard,
  ArrowRightLeft,
  ShieldX,
  LoaderCircle,
  ExternalLink, RefreshCw, ChevronLeft, ChevronRight, Check, CircleX, X, Eye, Send,
} from "lucide-react";
import { hasAccess } from "utils/access";
import { DateTime, Vehicle, StatusBadge } from "components/Erp/Display";
import { PageHeader, SectionHeading, EmptyState } from "components/Erp/Presentation";
import {
  getDriverCheckedList,
  approveDriverChecked,
  rejectDriverChecked,
  getBaseRequestsAdmin,
  getBaseRequestsForOperator,
  approveBaseRequest,
  rejectBaseRequest,
  getDocChangeRequestsAdmin,
  approveDocChangeRequest,
  rejectDocChangeRequest,
  getMyBaseRequests,
  getMyDocChangeRequests,
  getDriverCabinet,
  createBaseRequest,
} from "service/DriverRegistrationService";
import {
  getRequestStatistics,
  correctRegistration,
  correctBaseRequest,
} from "service/RequestsService";
import { getDriverDetail } from "service/AdminPanelService";
import {
  getReportDriverDetail,
  getReportRequests,
  getReportRequestStatistics,
} from "service/ReportsService";
import { getErpErrorMessage } from "service/ErpService";
import s from "pages/AdminPanelPage/Management.module.scss";
import requestStyles from "./RequestsPage.module.scss";
import RequestsTabs from './RequestsTabs/RequestsTabs.tsx';
import RequestTimeline from './RequestTimeline/RequestTimeline.tsx';
import RequestVehicleCorrection from './RequestVehicleCorrection/RequestVehicleCorrection.tsx';
import { normalizeVehicleNumber } from 'service/VehicleNumberService';

const CONFIG = {
  bases: {
    title: "Заявки на базы",
    icon: Warehouse,
    section: "base_requests",
    access: "requests",
    list: getBaseRequestsAdmin,
    approve: approveBaseRequest,
    reject: rejectBaseRequest,
    correct: correctBaseRequest,
  },
  registrations: {
    title: "Регистрация водителей",
    icon: UserCheck,
    section: "confirmations",
    access: "confirmations",
    list: getDriverCheckedList,
    approve: approveDriverChecked,
    reject: rejectDriverChecked,
    correct: correctRegistration,
  },
  documents: {
    title: "Изменение документов",
    icon: FileCheck2,
    section: "documents",
    access: "confirmations",
    list: getDocChangeRequestsAdmin,
    approve: approveDocChangeRequest,
    reject: rejectDocChangeRequest,
  },
};
const STATUS = {
  pending: "На рассмотрении",
  approved: "Одобрено",
  rejected: "Отказано",
};
const DOC_FIELDS = [
  ["photo_vehicle", "Автомобиль"],
  ["passport_front", "Паспорт — лицевая сторона"],
  ["passport_back", "Паспорт — обратная сторона"],
  ["license_front", "Удостоверение — лицевая сторона"],
  ["license_back", "Удостоверение — обратная сторона"],
  ["tech_passport_front", "Техпаспорт — лицевая сторона"],
  ["tech_passport_back", "Техпаспорт — обратная сторона"],
];
const nameOf = (item) =>
  item.driver_name ||
  [item.last_name, item.first_name, item.middle_name]
    .filter(Boolean)
    .join(" ") ||
  tr("Водитель #{{value0}}", { value0: item.driver || item.id });
const numberOf = (item) => item.vehicle_number || item.driver_vehicle || "—";
const toneOf = (status) =>
  status === "approved"
    ? "success"
    : status === "rejected"
      ? "danger"
      : "warning";

function Evidence({ item, type, readOnly = false }) {
  useInterfaceLanguage();
  const [preview, setPreview] = useState(null);
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let cancelled = false;
    setDriver(null);
    setError("");
    setPreview(null);
    if (type !== "bases" || !item.driver) {
      setLoading(false);
      return;
    }
    setLoading(true);
    (readOnly ? getReportDriverDetail : getDriverDetail)(item.driver)
      .then((res) => {
        if (!cancelled) setDriver(res.data);
      })
      .catch((e) => {
        if (!cancelled)
          setError(
            getErpErrorMessage(
              e,
              tr("Не удалось загрузить фотографии и документы водителя."),
            ),
          );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [type, item.driver, item.id, readOnly]);
  const documents =
    type === "registrations"
      ? DOC_FIELDS.map(([key, label]) => [
          label,
          item[`${key}_url`] || item[key],
        ])
      : type === "documents"
        ? [
            [tr("Действующий документ"), item.old_file_url],
            [tr("Новый документ"), item.new_file_url],
          ]
        : [
            ...DOC_FIELDS.map(([key, label]) => [
              label,
              driver?.[key + "_url"] || driver?.[key],
            ]),
            ...(driver?.documents || []).map((document) => [
              document.description || document.file_name || tr("Дополнительный документ"),
              document.file_url || document.file,
            ]),
            [tr("Фото водителя"), item.driver_photo_url],
          ];
  const present = documents.filter(([, url]) => url);
  return (
    <>
      {tr(error && (
        <p role="alert" className={s.error}>
          {tr(error)}
        </p>
      ))}
      {loading ? (
        <p role="status" className={s.muted}>{tr("Загрузка документов водителя…")}</p>
      ) : !present.length && !error ? (
        <EmptyState icon={FileText} title={tr("Фотографии и документы не приложены")} compact />
      ) : (
        <div className={`${s.documents} ${requestStyles.documents}`}>
          {present.map(([label, url]) => (
            <button
              type="button"
              className={`${s.button} ${preview === url ? s.tabActive : ""}`}
              key={`${label}-${url}`}
              onClick={() => setPreview((prev) => (prev === url ? null : url))}
            >
              <FileText size={14} />
              {tr(label)}
            </button>
          ))}
        </div>
      )}
      {tr(preview && (
        <div>
          {String(preview).toLowerCase().includes(".pdf") ? (
            <iframe
              className={`${s.preview} ${requestStyles.preview}`}
              src={preview}
              title={tr("Просмотр документа")}
              style={{ height: 400 }}
            />
          ) : (
            <img
              className={`${s.preview} ${requestStyles.preview}`}
              src={preview}
              alt={tr("Документ для проверки")}
            />
          )}
          <a
            className={s.button}
            href={preview}
            target="_blank"
            rel="noopener noreferrer"
          ><ExternalLink size={14} aria-hidden="true"/>{tr("Открыть оригинал")}</a>
        </div>
      ))}
    </>
  );
}

export { RequestTimeline };

export function RequestsQueue({ type = "bases", readOnly = false }) {
  useInterfaceLanguage();
  const user = useSelector((state) => state.user);
  const config = CONFIG[type];
  const operatorScope =
    !readOnly &&
    type === "bases" &&
    ["operator", "base_operator"].includes(user.erp_role) &&
    !hasAccess(user, "requests");
  const [records, setRecords] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState(readOnly ? "" : "pending");
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [history, setHistory] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [reason, setReason] = useState("");
  const [correcting, setCorrecting] = useState(null);
  const [plate, setPlate] = useState("");
  const [vehicleModel, setVehicleModel] = useState("");
  const [busy, setBusy] = useState(false);
  const [group, setGroup] = useState("all");
  const [statistics, setStatistics] = useState(null);
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [statisticsError, setStatisticsError] = useState("");
  const requestSequence = useRef(0);
  const statisticsSequence = useRef(0);
  const loadRecords = useCallback(async () => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    setError("");
    try {
      const params = {
        page,
        page_size: 20,
        ...(query ? { search: query } : {}),
      };
      const { data } = readOnly
        ? await getReportRequests({ type, status: status || "all", ...params })
        : operatorScope
        ? await getBaseRequestsForOperator({ ...params, status: status || "all" })
        : await config.list(status || null, params);
      if (sequence !== requestSequence.current) return;
      const rows = data.results || data || [];
      const count = data.count ?? rows.length;
      setRecords(rows);
      setTotal(count);
      if (page > 1 && (page - 1) * 20 >= count)
        setPage(Math.max(1, Math.ceil(count / 20)));
    } catch (e) {
      if (sequence === requestSequence.current)
        setError(getErpErrorMessage(e, tr("Не удалось загрузить заявки.")));
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [config, status, page, query, operatorScope, type, readOnly]);
  const loadStatistics = useCallback(async () => {
    const sequence = ++statisticsSequence.current;
    setStatisticsLoading(true);
    setStatisticsError("");
    try {
      const { data } = await (readOnly ? getReportRequestStatistics : getRequestStatistics)({
        type,
        ...(query ? { search: query } : {}),
      });
      if (sequence === statisticsSequence.current) setStatistics(data);
    } catch (e) {
      if (sequence === statisticsSequence.current)
        setStatisticsError(getErpErrorMessage(e, tr("Не удалось обновить количество заявок.")));
    } finally {
      if (sequence === statisticsSequence.current) setStatisticsLoading(false);
    }
  }, [type, query, readOnly]);
  const load = useCallback(
    () => Promise.all([loadRecords(), loadStatistics()]),
    [loadRecords, loadStatistics],
  );
  useEffect(() => {
    setPage(1);
    setExpanded(null);
    setHistory(null);
    setSearch("");
    setQuery("");
    setSuccess("");
  }, [type]);
  useEffect(() => {
    loadRecords();
    return () => { requestSequence.current += 1; };
  }, [loadRecords]);
  useEffect(() => {
    loadStatistics();
    return () => { statisticsSequence.current += 1; };
  }, [loadStatistics]);
  const selectStatus = (value) => {
    setStatus(value);
    setPage(1);
    setExpanded(null);
    setHistory(null);
  };
  const run = async (operation, message) => {
    if (readOnly) return;
    setBusy(true);
    setError("");
    try {
      const result = await operation();
      setSuccess(typeof result?.data?.detail === "string" ? result.data.detail : message);
      setRejecting(null);
      setCorrecting(null);
      await load();
    } catch (e) {
      setError(getErpErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };
  const mayAct =
    !readOnly && !user.is_report_only && (hasAccess(user, "confirmations") || operatorScope);
  const groups = records.reduce((result, item) => {
    const key =
      group === "driver"
        ? nameOf(item)
        : group === "base"
          ? item.base_name || tr("Без базы")
          : "";
    (result[key] ||= []).push(item);
    return result;
  }, {});
  return (
    <section className={s.panel}>
      <SectionHeading icon={config.icon} title={tr(config.title)}
          description={type === "bases"
              ? tr("Вход и выход из состава базы, переходы между базами и решения ответственных.")
              : type === "registrations"
                ? tr("Проверка личных данных, автомобиля и семи документов перед регистрацией водителя.")
                : tr("Сравнение текущего и нового документа перед подтверждением.")}
          actions={<button
            type="button"
            className={s.button}
            disabled={loading || statisticsLoading || busy}
            onClick={load}
          ><RefreshCw size={14} aria-hidden="true" />{tr(" Обновить")}</button>}
      />
      <div className={requestStyles.statistics} aria-label={tr("Количество заявок")} aria-busy={statisticsLoading}>
        {[
          ["total", tr("Всего заявок"), "", ClipboardList],
          ["pending", STATUS.pending, "pending", Clock3],
          ["approved", STATUS.approved, "approved", Check],
          ["rejected", STATUS.rejected, "rejected", CircleX],
          ["processed", tr("Обработано"), null, FileCheck2],
        ].map(([key, label, value, Icon]) => {
          const count = statistics?.[key] ?? "—";
          const content = <>
            <span className={requestStyles.statisticsLabel}><span>{tr(label)}</span><Icon size={17} aria-hidden="true" /></span>
            <strong className={requestStyles.statisticsValue}>{tr(count)}</strong>
            <span className={requestStyles.statisticsNote}>{key === "processed" ? tr("Одобрено и отказано") : query ? tr("По текущему поиску") : tr("За всё время")}</span>
          </>;
          const className = `${requestStyles.statisticsCard} ${requestStyles[key] || ""} ${value !== null && status === value ? requestStyles.statisticsActive : ""}`;
          return value === null ? (
            <div className={className} key={key} aria-label={`${tr(label)}: ${count}`}>{content}</div>
          ) : (
            <button
              type="button"
              className={className}
              key={key}
              aria-label={`${tr(label)}: ${count}`}
              aria-pressed={status === value}
              disabled={busy}
              onClick={() => selectStatus(value)}
            >{content}</button>
          );
        })}
      </div>
      {tr(statisticsError && <div className={s.error} role="alert">
        {tr(statisticsError)}
        <button type="button" className={s.button} onClick={loadStatistics} disabled={statisticsLoading}><RefreshCw size={14} aria-hidden="true" />{tr(" Повторить")}</button>
      </div>)}
      {tr(error && !rejecting && !correcting && (
        <div className={s.error} role="alert">
          {tr(error)}
          <button className={s.button} onClick={load}><RefreshCw size={14} aria-hidden="true"/>{tr("Повторить")}</button>
        </div>
      ))}
      {tr(success && (
        <div className={s.success} role="status">
          {tr(success)}
        </div>
      ))}
      <form
        className={s.toolbar}
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setExpanded(null);
          setHistory(null);
          setQuery(search.trim());
        }}
      >
        <input
          aria-label={tr("Поиск заявок")}
          placeholder={tr("ФИО, номер автомобиля, телефон")}
          value={search}
          disabled={busy}
          onChange={(e) => setSearch(e.target.value)}
        />
        <button className={s.button}>
          <Search size={14} />{tr("Найти")}</button>
        <select
          aria-label={tr("Статус заявки")}
          value={status}
          disabled={busy}
          onChange={(e) => selectStatus(e.target.value)}
        >
          <option value="">{tr("Все статусы")}</option>
          {Object.entries(STATUS).map(([key, label]) => (
            <option key={key} value={key}>
              {tr(label)}
            </option>
          ))}
        </select>
        <select
          aria-label={tr("Группировка заявок")}
          value={group}
          onChange={(e) => setGroup(e.target.value)}
        >
          <option value="all">{tr("Общий список")}</option>
          <option value="driver">{tr("По водителям")}</option>
          {tr(type === "bases" && <option value="base">{tr("По базам")}</option>)}
        </select>
      </form>
      {tr(operatorScope && <p className={s.muted}>{tr("Заявки и решения только по вашим базам.")}</p>)}
      {loading && !records.length ? (
        <EmptyState icon={LoaderCircle} title={tr("Загрузка заявок…")} compact />
      ) : !records.length && error ? null : !records.length ? (
        <EmptyState icon={config.icon} title={tr("Заявок по выбранным условиям нет")} description={tr("Выберите другой статус или измените поиск. Новые заявки появятся в этой очереди.")} compact />
      ) : (
        Object.entries(groups).map(([groupTitle, items]) => (
          <div key={groupTitle}>
            {tr(groupTitle && <h3 className={s.groupTitle}>{tr(groupTitle)}</h3>)}
            <div className={s.tableWrap}>
              <table className={`${s.table} ${requestStyles.table}`}>
                <colgroup className={requestStyles.queueColumns}>
                  <col /><col /><col /><col /><col /><col />
                </colgroup>
                <thead>
                  <tr>
                    <th>{tr("Заявка / водитель")}</th>
                    <th>{tr("Автомобиль")}</th>
                    <th>{type === "bases" ? tr("Направление") : tr("Данные")}</th>
                    <th>{tr("Подана")}</th>
                    <th>{tr("Статус")}</th>
                    <th>{tr("Подробности")}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <React.Fragment key={item.id}>
                      <tr>
                        <td data-label={tr("Заявка / водитель")}>
                          <strong>{nameOf(item)}</strong>
                          <div className={s.muted}>
                            #{tr(item.id)} ·{" "}
                            {tr(item.phone || item.driver_phone || "—")}
                          </div>
                        </td>
                        <td data-label={tr("Автомобиль")}>
                          <Vehicle
                            number={numberOf(item)}
                            model={item.vehicle_model}
                          />
                        </td>
                        <td
                          data-label={
                            tr(type === "bases" ? tr("Направление") : tr("Данные"))
                          }
                        >
                          {type === "bases" ? (
                            <>
                              <StatusBadge
                                icon={false}
                                tone={
                                  item.request_type === "leave"
                                    ? "warning"
                                    : "success"
                                }
                              >
                                {item.request_type === "leave" ? (
                                  <ArrowUpRight size={12} aria-hidden="true" />
                                ) : (
                                  <ArrowDownLeft size={12} aria-hidden="true" />
                                )}{" "}
                                {item.request_type === "leave"
                                  ? tr("ВЫХОД")
                                  : tr("ВХОД")}
                              </StatusBadge>
                              <div>{tr(item.base_name || "—")}</div>
                              {tr(item.new_base_name && (
                                <div className={s.muted}>{tr("Следующая база: ")}{tr(item.new_base_name)}
                                </div>
                              ))}
                            </>
                          ) : type === "documents" ? (
                            item.field_label || item.field_name
                          ) : (
                            <div>{tr("ИНН: ")}{tr(item.inn || "—")}</div>
                          )}
                        </td>
                        <td data-label={tr("Подана")}>
                          <DateTime value={item.created_at} />
                        </td>
                        <td data-label={tr("Статус")}>
                          <StatusBadge tone={toneOf(item.status)}>
                            {tr(STATUS[item.status] || item.status)}
                          </StatusBadge>
                          {tr(item.reviewed_by_name && (
                            <div className={s.muted}>
                              {tr(item.reviewed_by_name)}
                            </div>
                          ))}
                        </td>
                        <td data-label={tr("Подробности")}>
                          <button
                            className={s.button}
                            aria-expanded={expanded === item.id}
                            onClick={() => {
                              setExpanded((prev) =>
                                prev === item.id ? null : item.id,
                              );
                              setHistory(null);
                            }}
                          >
                            {expanded === item.id ? (
                              <ChevronUp size={14} />
                            ) : (
                              <ChevronDown size={14} />
                            )}{tr("Открыть")}</button>
                        </td>
                      </tr>
                      {tr(expanded === item.id && (
                        <tr className={requestStyles.detailRow}>
                          <td colSpan={6} className={requestStyles.detailCell}>
                            <div className={`${s.detail} ${requestStyles.detailContent}`}>
                              <div className={s.grid}>
                                <div>
                                  <span className={s.muted}>{tr("Водитель")}</span>
                                  <p>
                                    {nameOf(item)} ·{" "}
                                    {tr(item.phone || item.driver_phone || "—")}
                                  </p>
                                </div>
                                <div>
                                  <span className={s.muted}>{tr("Изменена")}</span>
                                  <DateTime value={item.updated_at} />
                                </div>
                                {tr(item.reviewed_by_name && (
                                  <div>
                                    <span className={s.muted}>{tr("Ответственный")}</span>
                                    <p>{tr(item.reviewed_by_name)}</p>
                                  </div>
                                ))}
                              </div>
                              <Evidence
                                key={`${type}-${item.id}`}
                                item={item}
                                type={type}
                                readOnly={readOnly}
                              />
                              {tr(item.rejection_reason && (
                                <div className={s.error}>
                                  <strong>{tr("Причина отказа: ")}</strong>
                                  {tr(item.rejection_reason)}
                                </div>
                              ))}
                              <div className={s.actions}>
                                {tr(mayAct && item.status === "pending" && (
                                  <>
                                    <button
                                      className={s.primary}
                                      disabled={busy || loading}
                                      onClick={() =>
                                        run(
                                          () => config.approve(item.id),
                                          type === "registrations"
                                            ? tr("Водитель подтверждён и зарегистрирован.")
                                            : type === "documents"
                                              ? tr("Документ подтверждён и заменён. Решение сохранено в истории.")
                                            : tr("Заявка подтверждена. Участие водителя в базе обновлено."),
                                        )
                                      }
                                    ><Check size={14} aria-hidden="true"/>{tr("Подтвердить")}</button>
                                    <button
                                      className={s.danger}
                                      disabled={busy || loading}
                                      onClick={() => {
                                        setRejecting(item);
                                        setReason("");
                                        setError("");
                                      }}
                                    ><CircleX size={14} aria-hidden="true"/>{tr("Отклонить")}</button>
                                  </>
                                ))}
                                {tr(!readOnly && hasAccess(user, "requests") &&
                                  (!item.driver ||
                                    hasAccess(user, "drivers_edit")) &&
                                  config.correct && (
                                    <button
                                      className={s.button}
                                      onClick={() => {
                                        setCorrecting(item);
                                        setPlate(normalizeVehicleNumber(item.vehicle_number || ""));
                                        setVehicleModel(
                                          item.vehicle_model || "",
                                        );
                                        setError("");
                                      }}
                                    >
                                      <Pencil size={14} />{tr("Исправить номер автомобиля")}</button>
                                  ))}
                                <button
                                  className={s.button}
                                  onClick={() =>
                                    setHistory((prev) =>
                                      prev === item.id ? null : item.id,
                                    )
                                  }
                                >
                                  <History size={14} />{tr("История заявки")}</button>
                              </div>
                              {tr(history === item.id && (
                                <RequestTimeline
                                  section={config.section}
                                  objectId={item.id}
                                  readOnly={readOnly}
                                />
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))
      )}
      <div className={s.pagination}>
        <span>{tr("Всего: ")}{tr(total)}{tr(" · Страница ")}{tr(page)}
        </span>
        <button
          className={s.button}
          disabled={page <= 1 || loading || busy}
          onClick={() => setPage((p) => p - 1)}
        ><ChevronLeft size={14} aria-hidden="true"/>{tr("Назад")}</button>
        <button
          className={s.button}
          disabled={page * 20 >= total || loading || busy}
          onClick={() => setPage((p) => p + 1)}
        ><ChevronRight size={14} aria-hidden="true"/>{tr("Далее")}</button>
      </div>
      {tr(!readOnly && rejecting && (
        <div className={s.overlay}>
          <form
            className={s.dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="request-reject-title"
            onSubmit={(e) => {
              e.preventDefault();
              run(
                () => config.reject(rejecting.id, reason.trim()),
                tr("Заявка отклонена. Причина сохранена в истории."),
              );
            }}
          >
            <h2 id="request-reject-title"><CircleX size={20} aria-hidden="true"/>{tr(" Отклонение заявки #")}{tr(rejecting.id)}</h2>
            <p>{nameOf(rejecting)}</p>
            {tr(error && (
              <div className={s.error} role="alert">
                {tr(error)}
              </div>
            ))}
            <label className={s.field}>{tr("Причина отказа")}<textarea
                required
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={4}
                placeholder={tr("Укажите, что нужно исправить водителю")}
              />
            </label>
            <div className={s.footer}>
              <button
                type="button"
                className={s.button}
                disabled={busy}
                onClick={() => setRejecting(null)}
              ><X size={14} aria-hidden="true"/>{tr("Отмена")}</button>
              <button className={s.danger} disabled={busy || !reason.trim()}><CircleX size={14} aria-hidden="true"/>{tr("Отклонить")}</button>
            </div>
          </form>
        </div>
      ))}
      {tr(!readOnly && correcting && (
        <RequestVehicleCorrection id={correcting.id} plate={plate} model={vehicleModel} error={error} busy={busy}
          onPlate={setPlate} onModel={setVehicleModel} onClose={() => setCorrecting(null)}
          onSave={() => run(
                () =>
                  config.correct(correcting.id, {
                    vehicle_number: plate.trim(),
                    ...(vehicleModel.trim() !== (correcting.vehicle_model || "")
                      ? { vehicle_model: vehicleModel.trim() }
                      : {}),
                  }),
                tr("Номер исправлен. Изменение сохранено в истории."),
              )}>
            <Evidence item={correcting} type={type} />
        </RequestVehicleCorrection>
      ))}
    </section>
  );
}

function DriverRequests() {
  useInterfaceLanguage();
  const [records, setRecords] = useState([]);
  const [cabinet, setCabinet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [creating, setCreating] = useState(false);
  const [locations, setLocations] = useState([]);
  const [location, setLocation] = useState("");
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [history, setHistory] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const profile = await getDriverCabinet();
      setCabinet(profile.data);
      const [bases, docs] = profile.data.driver
        ? await Promise.all([getMyBaseRequests(), getMyDocChangeRequests()])
        : [{ data: [] }, { data: [] }];
      setRecords(
        [
          ...(profile.data.driver_checked
            ? [{ ...profile.data.driver_checked, kind: "registrations" }]
            : []),
          ...(bases.data.results || bases.data || []).map((item) => ({
            ...item,
            kind: "bases",
          })),
          ...(docs.data.results || docs.data || []).map((item) => ({
            ...item,
            kind: "documents",
          })),
        ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
      );
    } catch (e) {
      setError(getErpErrorMessage(e, tr("Не удалось загрузить ваши заявки.")));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    load();
  }, [load]);
  const openCreate = async () => {
    setCreating(true);
    setLocation("");
    setError("");
    try {
      const { data } = await $api.get("/tabel/registration-locations/");
      setLocations(
        (data.results || data || []).filter(
          (item) => item.name !== "Черный список",
        ),
      );
    } catch (e) {
      setError(getErpErrorMessage(e, tr("Не удалось загрузить базы. Закройте окно и повторите.")));
    }
  };
  const hasBase = !!(
    cabinet?.driver?.top_base ||
    cabinet?.driver?.chpp ||
    cabinet?.driver?.private_point
  );
  const submit = async (event) => {
    event.preventDefault();
    const target = locations.find(
      (item) => `${item.type}:${item.id}` === location,
    );
    if (!target) return;
    setBusy(true);
    setError("");
    try {
      await createBaseRequest({
        request_type: hasBase ? "leave" : "join",
        [`${hasBase ? "new_" : ""}${target.type}`]: target.id,
      });
      setCreating(false);
      setSuccess(
        tr("Заявка отправлена. Решение и причина отказа появятся в этом разделе."),
      );
      await load();
    } catch (e) {
      setError(getErpErrorMessage(e, tr("Не удалось отправить заявку.")));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className={s.panel}>
      <SectionHeading icon={ClipboardList} title={tr("Мои заявки")} description={tr("Участие в базах, смена базы и проверка документов.")}
        actions={<div className={s.actions}>
          {tr(cabinet?.driver && (
            <button
              className={s.primary}
              disabled={!!cabinet.pending_base_request}
              onClick={openCreate}
            >
              <ArrowRightLeft size={15} aria-hidden="true" />
              {hasBase ? tr("Сменить базу") : tr("Подать заявку на базу")}
            </button>
          ))}
          <Link className={s.button} to="/report-card/">
            <LayoutDashboard size={15} aria-hidden="true" />{tr("Кабинет водителя")}</Link>
          <button
            className={s.button}
            disabled={loading || !cabinet}
            onClick={() => setHistory((value) => !value)}
          >
            <History size={14} />{tr("Общая история")}</button>
        </div>}
      />
      {tr(cabinet?.pending_base_request && (
        <p className={s.muted}>{tr("Заявка на базу уже рассматривается. Дождитесь решения ответственного.")}</p>
      ))}
      {tr(!cabinet?.driver && cabinet && (
        <p className={s.muted}>{tr("Заявки на базы станут доступны после подтверждения регистрации.")}</p>
      ))}
      {tr(error && !creating && (
        <div className={s.error} role="alert">
          {tr(error)}
          <button className={s.button} onClick={load}><RefreshCw size={14} aria-hidden="true"/>{tr("Повторить")}</button>
        </div>
      ))}
      {tr(success && (
        <div className={s.success} role="status">
          {tr(success)}
        </div>
      ))}
      {history ? (
        <RequestTimeline
          grouped
          driver={cabinet?.driver?.id || ""}
          section={cabinet?.driver ? "" : "confirmations"}
          objectId={cabinet?.driver ? "" : cabinet?.driver_checked?.id || ""}
        />
      ) : loading ? (
        <EmptyState icon={LoaderCircle} title={tr("Загрузка заявок…")} compact />
      ) : !records.length && error ? null : !records.length ? (
        <EmptyState icon={ClipboardList} title={tr("Заявок пока нет")} description={tr("Подайте заявку на базу. Решение оператора и история рассмотрения появятся здесь.")} compact />
      ) : (
        <div className={s.tableWrap}>
          <table className={`${s.table} ${requestStyles.table}`}>
            <colgroup className={requestStyles.personalColumns}>
              <col /><col /><col /><col /><col />
            </colgroup>
            <thead>
              <tr>
                <th>{tr("Заявка")}</th>
                <th>{tr("Направление / документ")}</th>
                <th>{tr("Подана")}</th>
                <th>{tr("Статус")}</th>
                <th>{tr("Подробности")}</th>
              </tr>
            </thead>
            <tbody>
              {records.map((item) => (
                <React.Fragment key={`${item.kind}-${item.id}`}>
                  <tr>
                    <td data-label={tr("Заявка")}>
                      #{tr(item.id)}
                      <div className={s.muted}>
                        {item.kind === "bases"
                          ? tr("Заявка на базу")
                          : item.kind === "registrations"
                            ? tr("Регистрация водителя")
                            : tr("Изменение документа")}
                      </div>
                    </td>
                    <td data-label={tr("Направление / документ")}>
                      {item.kind === "bases" ? (
                        <>
                          <StatusBadge
                            icon={item.request_type === "leave" ? ArrowUpRight : ArrowDownLeft}
                            tone={
                              item.request_type === "leave"
                                ? "warning"
                                : "success"
                            }
                          >
                            {item.request_type === "leave" ? tr("ВЫХОД") : tr("ВХОД")}
                          </StatusBadge>
                          <div>
                            {tr(item.base_name || "—")}
                            {item.new_base_name
                              ? ` → ${item.new_base_name}`
                              : ""}
                          </div>
                        </>
                      ) : item.kind === "registrations" ? (
                        <Vehicle
                          number={item.vehicle_number}
                          model={item.vehicle_model}
                        />
                      ) : (
                        item.field_label || item.field_name
                      )}
                    </td>
                    <td data-label={tr("Подана")}>
                      <DateTime value={item.created_at} />
                    </td>
                    <td data-label={tr("Статус")}>
                      <StatusBadge tone={toneOf(item.status)}>
                        {tr(STATUS[item.status] || item.status)}
                      </StatusBadge>
                    </td>
                    <td data-label={tr("Подробности")}>
                      <button
                        className={s.button}
                        onClick={() =>
                          setExpanded((prev) =>
                            prev === `${item.kind}-${item.id}`
                              ? null
                              : `${item.kind}-${item.id}`,
                          )
                        }
                      ><Eye size={14} aria-hidden="true"/>{tr("Открыть")}</button>
                    </td>
                  </tr>
                  {tr(expanded === `${item.kind}-${item.id}` && (
                    <tr className={requestStyles.detailRow}>
                      <td colSpan={5} className={`${requestStyles.detailCell} ${requestStyles.detailContent}`}>
                        {tr(item.rejection_reason && (
                          <p className={s.error}>{tr("Причина отказа: ")}{tr(item.rejection_reason)}
                          </p>
                        ))}
                        {tr(item.kind !== "bases" && (
                          <Evidence item={item} type={item.kind} />
                        ))}
                        <RequestTimeline
                          section={
                            item.kind === "bases"
                              ? "base_requests"
                              : item.kind === "registrations"
                                ? "confirmations"
                                : "documents"
                          }
                          objectId={item.id}
                        />
                      </td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {tr(creating && (
        <div className={s.overlay}>
          <form
            className={s.dialog}
            role="dialog"
            aria-modal="true"
            aria-labelledby="driver-request-title"
            onSubmit={submit}
          >
            <h2 id="driver-request-title">
              <Warehouse size={20} aria-hidden="true"/>
              {hasBase ? tr("Смена базы") : tr("Заявка на базу")}
            </h2>
            <p className={s.muted}>
              {hasBase
                ? tr("После подтверждения выхода текущая база будет снята и создана заявка на вход в выбранную базу.")
                : tr("Оператор выбранной базы проверит и подтвердит заявку.")}
            </p>
            {tr(error && (
              <div className={s.error} role="alert">
                {tr(error)}
              </div>
            ))}
            <label className={s.field}>{tr("База")}<select
                required
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              >
                <option value="">{tr("Выберите базу")}</option>
                {locations.map((item) => (
                  <option
                    key={`${item.type}-${item.id}`}
                    value={`${item.type}:${item.id}`}
                  >
                    {tr(item.name)} ·{" "}
                    {item.type === "top_base"
                      ? tr("ТОП-база")
                      : item.type === "chpp"
                        ? tr("ТЭЦ")
                        : tr("Социальный объект")}
                  </option>
                ))}
              </select>
            </label>
            <div className={s.footer}>
              <button
                type="button"
                className={s.button}
                disabled={busy}
                onClick={() => setCreating(false)}
              ><X size={14} aria-hidden="true"/>{tr("Отмена")}</button>
              <button className={s.primary} disabled={busy || !location}><Send size={14} aria-hidden="true"/>{tr("Отправить заявку")}</button>
            </div>
          </form>
        </div>
      ))}
    </section>
  );
}

export function RequestsReport() {
  useInterfaceLanguage();
  const [tab, setTab] = useState("bases");
  return <div>
    <div className={s.tabs} aria-label={tr("Разделы отчёта по заявкам")}>
      {[
        ["bases", tr("Заявки на базы"), Warehouse],
        ["registrations", tr("Подтверждения"), UserCheck],
        ["documents", tr("Документы"), FileCheck2],
        ["history", tr("История"), History],
      ].map(([key, label, Icon]) => <button
        type="button"
        key={key}
        className={`${s.tab} ${tab === key ? s.tabActive : ""}`}
        onClick={() => setTab(key)}
        aria-pressed={tab === key}
      ><Icon size={16} aria-hidden="true" />{tr(label)}</button>)}
    </div>
    {tab === "history" ? <section className={s.panel}>
      <SectionHeading icon={History} title={tr("История заявок")} description={tr("Решения, изменения, время события и ответственный. Группировка относится к выбранной странице.")} />
      <RequestTimeline grouped readOnly />
    </section> : <RequestsQueue key={tab} type={tab} readOnly />}
  </div>;
}

export default function RequestsPage() {
  useInterfaceLanguage();
  const user = useSelector((state) => state.user);
  const tabs = React.useMemo(
    () =>
      [
        user.erp_role === "driver" && ["mine", tr("Мои заявки")],
        (hasAccess(user, "requests") ||
          hasAccess(user, "confirmations") ||
          ["operator", "base_operator"].includes(user.erp_role)) && [
          "bases",
          tr("Заявки на базы"),
        ],
        (hasAccess(user, "requests") || hasAccess(user, "confirmations")) && [
          "registrations",
          tr("Подтверждения"),
        ],
        (hasAccess(user, "requests") || hasAccess(user, "confirmations")) && [
          "documents",
          tr("Документы"),
        ],
        (hasAccess(user, "requests") ||
          hasAccess(user, "confirmations") ||
          ["operator", "base_operator"].includes(user.erp_role)) && [
          "history",
          tr("История"),
        ],
      ].filter(Boolean),
    [user],
  );
  const [tab, setTab] = useState(tabs[0]?.[0] || "bases");
  useEffect(() => {
    if (tabs.length && !tabs.some(([key]) => key === tab)) setTab(tabs[0][0]);
  }, [tabs, tab]);
  return (
    <Layout>
      <div style={{ minWidth: 0, padding: "8px 0" }}>
        <PageHeader icon={ClipboardList} title={tr("Заявки")} description={tr("Регистрация, участие в базах и проверка документов.")} />
        <RequestsTabs tabs={tabs} tab={tab} onChange={setTab} />
        {tab === "mine" && user.erp_role === "driver" ? (
          <DriverRequests />
        ) : !tabs.length ? (
          <div className={s.panel}><EmptyState icon={ShieldX} title={tr("Нет доступа к заявкам.")} description={tr("Разрешения на просмотр и подтверждение задаёт администратор.")} compact /></div>
        ) : tab === "history" ? (
          <section className={s.panel}>
            <SectionHeading icon={History} title={tr("История заявок")} description={tr("Группировка относится к выбранной странице. Используйте поиск, чтобы увидеть конкретного водителя или базу.")} />
            <RequestTimeline grouped />
          </section>
        ) : (
          <RequestsQueue key={tab} type={tab} />
        )}
      </div>
    </Layout>
  );
}
